import Label from '@/components/Label'
import clsx from 'clsx'
import { useState } from 'react'
import { isValidCNP } from '@/lib/cnp'

export default function DuplicateStareCivilaForm({
  register,
  profile,
  errors,
  setValue,
}) {
  const [selectedDocumentType, setSelectedDocumentType] = useState('')

  const documentTypes = [
    { value: 'certificat-nastere', label: 'Certificat de naștere' },
    { value: 'certificat-casatorie', label: 'Certificat de căsătorie' },
    { value: 'certificat-deces', label: 'Certificat de deces' },
    {
      value: 'extras-multilingv-nastere',
      label: 'Extras multilingv de naștere',
    },
    {
      value: 'extras-multilingv-casatorie',
      label: 'Extras multilingv de căsătorie',
    },
    { value: 'extras-multilingv-deces', label: 'Extras multilingv de deces' },
    {
      value: 'adeverinta-celibat',
      label: 'Adeverință de celibat (de stare civilă)',
    },
  ]

  // Document types that require CNP field
  const cnpRequiredTypes = [
    'extras-multilingv-nastere',
    'extras-multilingv-casatorie',
    'extras-multilingv-deces',
  ]

  // Document types for birth-related documents
  const birthDocTypes = [
    'certificat-nastere',
    'extras-multilingv-nastere',
    'adeverinta-celibat',
  ]

  // Document types for marriage-related documents
  const marriageDocTypes = [
    'certificat-casatorie',
    'extras-multilingv-casatorie',
  ]

  // Document types for death-related documents
  const deathDocTypes = ['certificat-deces', 'extras-multilingv-deces']

  // Function to extract and format date from CNP
  const extractDateFromCNP = (cnp) => {
    if (!cnp || cnp.length < 6) return ''

    const year = cnp.substring(1, 3)
    const month = cnp.substring(3, 5)
    const day = cnp.substring(5, 7)

    // Determine century based on first digit
    const firstDigit = cnp.charAt(0)
    let fullYear

    if (firstDigit === '1' || firstDigit === '2') {
      fullYear = '19' + year
    } else if (firstDigit === '5' || firstDigit === '6') {
      fullYear = '20' + year
    } else {
      return '' // Invalid or unsupported CNP format
    }

    return `${day}.${month}.${fullYear}`
  }

  // Function to handle CNP change and auto-fill date fields
  const handleCNPChange = (cnp, fieldContext = null) => {
    if (cnp && cnp.length === 13 && /^\d{13}$/.test(cnp)) {
      if (isValidCNP(cnp)) {
        const extractedDate = extractDateFromCNP(cnp)
        if (extractedDate) {
          // Fill birth date for birth-related documents
          if (birthDocTypes.includes(selectedDocumentType)) {
            setValue('dataNasterii', extractedDate)
          }
        }
      }
    }
  }

  return (
    <>
      <div className="mt-4">
        <Label text="Nume solicitant" forId="nume" required={true} />
        <input
          type="text"
          id="nume"
          defaultValue={profile?.last_name}
          {...register('nume', {
            required: true,
          })}
          className={clsx(
            'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
            errors.nume ? 'border-red-500 border-2' : 'border-slate-300'
          )}
        />
        {errors.nume && (
          <div className="text-red-500 py-1">Nume este obligatoriu</div>
        )}
      </div>

      <div className="mt-4">
        <Label text="Prenume solicitant" forId="prenume" required={true} />
        <input
          type="text"
          id="prenume"
          defaultValue={profile?.first_name}
          {...register('prenume', {
            required: true,
          })}
          className={clsx(
            'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
            errors.prenume ? 'border-red-500 border-2' : 'border-slate-300'
          )}
        />
        {errors.prenume && (
          <div className="text-red-500 py-1">Prenume este obligatoriu</div>
        )}
      </div>

      <div className="mt-12 md:mt-4 md:col-span-2">
        <Label
          text="Vă rugăm să alegeți documentul solicitat"
          forId="tip-document"
          required={true}
        />
        <select
          id="tip-document"
          {...register('tipDocument', {
            required: true,
            onChange: (e) => setSelectedDocumentType(e.target.value),
          })}
          className={clsx(
            'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark',
            errors.tipDocument ? 'border-red-500 border-2' : 'border-slate-300'
          )}
        >
          <option value="">Selectați tipul documentului</option>
          {documentTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {errors.tipDocument && (
          <div className="text-red-500 py-1">
            Tipul documentului este obligatoriu
          </div>
        )}
      </div>

      {/* Information box when no document type is selected */}
      {!selectedDocumentType && (
        <div className="mt-4 md:col-span-2">
          <div className="bg-blue-50 border-l-4 border-primary p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-primary mb-0">
                  <strong>Atenție:</strong> Puteți solicita doar un singur tip
                  de document per programare. Pentru documente diferite este
                  necesar să faceți programări separate.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CNP field - optional for all document types, shown only after document type selection */}
      {selectedDocumentType && (
        <div className="mt-12 md:mt-4 md:col-span-2">
          <Label
            text={
              selectedDocumentType.includes('extras-multilingv')
                ? 'CNP (de cetățean român) al titularului actului'
                : marriageDocTypes.includes(selectedDocumentType)
                ? 'CNP (soț sau soție)'
                : 'CNP'
            }
            forId="cnp"
            required={false}
          />
          <input
            type="text"
            id="cnp"
            {...register('cnp', {
              required: false,
              validate: (v) => {
                if (!v || v.length === 0) return true
                if (v.length !== 13) return 'CNP trebuie să aibă 13 cifre'
                if (!/^\d{13}$/.test(v))
                  return 'CNP trebuie să conțină doar cifre'

                if (!isValidCNP(v)) {
                  return 'CNP invalid'
                }

                return true
              },
              onChange: (e) => handleCNPChange(e.target.value),
            })}
            className={clsx(
              'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
              errors.cnp ? 'border-red-500 border-2' : 'border-slate-300'
            )}
            placeholder="13 cifre"
          />
          {errors.cnp && (
            <div className="text-red-500 py-1">{errors.cnp.message}</div>
          )}
        </div>
      )}

      {/* Fields for birth-related documents */}
      {birthDocTypes.includes(selectedDocumentType) && (
        <>
          <div className="mt-4">
            <Label
              text="Numele dobândit la naștere al titularului"
              forId="nume-nastere"
              required={true}
            />
            <input
              type="text"
              id="nume-nastere"
              {...register('numeNastere', {
                required: birthDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.numeNastere
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.numeNastere && (
              <div className="text-red-500 py-1">
                Numele dobândit la naștere este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-4">
            <Label
              text="Prenumele dobândit la naștere al titularului"
              forId="prenume-nastere"
              required={true}
            />
            <input
              type="text"
              id="prenume-nastere"
              {...register('prenumeNastere', {
                required: birthDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.prenumeNastere
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.prenumeNastere && (
              <div className="text-red-500 py-1">
                Prenumele dobândit la naștere este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-12 md:mt-4">
            <Label text="Data nașterii" forId="data-nasterii" required={true} />
            <input
              type="text"
              id="data-nasterii"
              placeholder="zz.ll.aaaa"
              {...register('dataNasterii', {
                required: birthDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.dataNasterii
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.dataNasterii && (
              <div className="text-red-500 py-1">
                Data nașterii este obligatorie
              </div>
            )}
          </div>

          <div className="mt-4 md:col-span-2">
            <Label
              text="Locul nașterii (localitate + județ/țară)"
              forId="loc-nasterii"
              required={true}
            />
            <input
              type="text"
              id="loc-nasterii"
              {...register('locNasterii', {
                required: birthDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.locNasterii
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
              placeholder="ex: Timișoara, România"
            />
            {errors.locNasterii && (
              <div className="text-red-500 py-1">
                Locul nașterii este obligatoriu
              </div>
            )}
          </div>
        </>
      )}

      {/* Fields for marriage-related documents */}
      {marriageDocTypes.includes(selectedDocumentType) && (
        <>
          <div className="mt-12 md:mt-4">
            <Label text="Numele soțului" forId="nume-sot" required={true} />
            <input
              type="text"
              id="nume-sot"
              {...register('numeSot', {
                required: marriageDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.numeSot ? 'border-red-500 border-2' : 'border-slate-300'
              )}
            />
            {errors.numeSot && (
              <div className="text-red-500 py-1">
                Numele soțului este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-4">
            <Label
              text="Prenumele soțului"
              forId="prenume-sot"
              required={true}
            />
            <input
              type="text"
              id="prenume-sot"
              {...register('prenumeSot', {
                required: marriageDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.prenumeSot
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.prenumeSot && (
              <div className="text-red-500 py-1">
                Prenumele soțului este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-12 md:mt-4">
            <Label text="Numele soției" forId="nume-sotie" required={true} />
            <input
              type="text"
              id="nume-sotie"
              {...register('numeSotie', {
                required: marriageDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.numeSotie
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.numeSotie && (
              <div className="text-red-500 py-1">
                Numele soției este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-4">
            <Label
              text="Prenumele soției"
              forId="prenume-sotie"
              required={true}
            />
            <input
              type="text"
              id="prenume-sotie"
              {...register('prenumeSotie', {
                required: marriageDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.prenumeSotie
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.prenumeSotie && (
              <div className="text-red-500 py-1">
                Prenumele soției este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-12 md:mt-4">
            <Label
              text="Data căsătoriei"
              forId="data-casatoriei"
              required={true}
            />
            <input
              type="text"
              id="data-casatoriei"
              placeholder="zz.ll.aaaa"
              {...register('dataCasatoriei', {
                required: marriageDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.dataCasatoriei
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.dataCasatoriei && (
              <div className="text-red-500 py-1">
                Data căsătoriei este obligatorie
              </div>
            )}
          </div>

          <div className="mt-4 md:col-span-2">
            <Label
              text="Locul căsătoriei (localitate + județ/țară)"
              forId="loc-casatoriei"
              required={true}
            />
            <input
              type="text"
              id="loc-casatoriei"
              {...register('locCasatoriei', {
                required: marriageDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.locCasatoriei
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
              placeholder="ex: Timișoara, România"
            />
            {errors.locCasatoriei && (
              <div className="text-red-500 py-1">
                Locul căsătoriei este obligatoriu
              </div>
            )}
          </div>
        </>
      )}

      {/* Fields for death-related documents */}
      {deathDocTypes.includes(selectedDocumentType) && (
        <>
          <div className="mt-12 md:mt-4">
            <Label
              text="Numele persoanei decedate"
              forId="nume-decedat"
              required={true}
            />
            <input
              type="text"
              id="nume-decedat"
              {...register('numeDecedat', {
                required: deathDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.numeDecedat
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.numeDecedat && (
              <div className="text-red-500 py-1">
                Numele persoanei decedate este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-4">
            <Label
              text="Prenumele persoanei decedate"
              forId="prenume-decedat"
              required={true}
            />
            <input
              type="text"
              id="prenume-decedat"
              {...register('prenumeDecedat', {
                required: deathDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.prenumeDecedat
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.prenumeDecedat && (
              <div className="text-red-500 py-1">
                Prenumele persoanei decedate este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-12 md:mt-4">
            <Label
              text="Data decesului"
              forId="data-decesului"
              required={true}
            />
            <input
              type="text"
              id="data-decesului"
              placeholder="zz.ll.aaaa"
              {...register('dataDecesului', {
                required: deathDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.dataDecesului
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
            />
            {errors.dataDecesului && (
              <div className="text-red-500 py-1">
                Data decesului este obligatorie
              </div>
            )}
          </div>

          <div className="mt-4 md:col-span-2">
            <Label
              text="Locul decesului (localitate + județ/țară)"
              forId="loc-decesului"
              required={true}
            />
            <input
              type="text"
              id="loc-decesului"
              {...register('locDecesului', {
                required: deathDocTypes.includes(selectedDocumentType),
              })}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                errors.locDecesului
                  ? 'border-red-500 border-2'
                  : 'border-slate-300'
              )}
              placeholder="ex: Timișoara, România"
            />
            {errors.locDecesului && (
              <div className="text-red-500 py-1">
                Locul decesului este obligatoriu
              </div>
            )}
          </div>

          <div className="mt-4 md:col-span-2">
            <Label
              text="Alte informații utile identificării actului de deces"
              forId="alte-informatii-deces"
              required={false}
            />
            <textarea
              id="alte-informatii-deces"
              {...register('alteInformatiiDeces')}
              className={clsx(
                'group block w-full px-2 py-2 mt-1 text-lg border font-medium rounded-none focus:outline-none focus:ring-4 focus:ring-primary-light focus:border-primary-dark placeholder-normal',
                'border-slate-300'
              )}
              rows={3}
              placeholder="Data nașterii, locul nașterii, prenumele părinților..."
            />
          </div>
        </>
      )}
    </>
  )
}
